import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { H5 } from 'foundations-components/transitional/components/H5';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { useCallback, useReducer } from 'react';
import * as http from 'reporting-data/request/http';
import { STANDARD_FIELD_TYPES } from 'reporting-ui-components/lib/report-strategies/shared/constants/fieldTypes';
import { getStandardFieldType } from 'reporting-ui-components/lib/report-strategies/shared/utils/reportGetters';
import useCloneTracker from '../../../hooks/useCloneTracker';
import { USER_FEEDBACK_URL } from '../../singleReportViewer/fullPage/report-ai-assistant-insights/utils';
import { feedbackFormStateReducer, initialState } from './FeedbackFormStateReducer';
import FeedbackPopoverBody from './FeedbackPopoverBody';
import { sendReportingInsightsUserFeedbackForReportingBackend } from './api';
import { REPORTING_AI_ASSISTANT_FEEDBACK_TYPE } from './feedbackConstants';
import { isReportValidForReportGetterStrategy } from 'reporting-ui-components/lib/report-strategies/utils/strategyFetchers';
import { jsx as _jsx } from "react/jsx-runtime";
const FeedbackPopoverContainer = ({
  children,
  feedbackType,
  popoverOpen,
  setPopoverOpen,
  insight,
  appUsageTracker,
  setDisableFeedback,
  report,
  dataset,
  correlationId
}) => {
  const [formState, formDispatch] = useReducer(feedbackFormStateReducer, initialState);
  const usageTracker = useCloneTracker(appUsageTracker);
  const isValidForReportGetterStrategy = report && isReportValidForReportGetterStrategy(report);
  const title = feedbackType === REPORTING_AI_ASSISTANT_FEEDBACK_TYPE.POSITIVE ? I18n.text('reporting-enablement.platformReport.reportVisualizationInsights.feedback.popover.title.positive') : I18n.text('reporting-enablement.platformReport.reportVisualizationInsights.feedback.popover.title.negative');
  const onSubmitFeedbackClick = useCallback(() => {
    setPopoverOpen(false);
    setDisableFeedback(true);
    Object.keys(formState.checkboxInputFeedback).forEach(key => {
      usageTracker.track('reportingAIAssistantInsightFeedback', {
        feedbackValue: I18n.text(`reporting-enablement.platformReport.reportVisualizationInsights.feedback.${feedbackType}.options.${key}`),
        isPositiveFeedbackType: feedbackType === REPORTING_AI_ASSISTANT_FEEDBACK_TYPE.POSITIVE,
        contactMe: formState.contactMe,
        insight: insight.value,
        chartType: isValidForReportGetterStrategy ? getStandardFieldType(report, STANDARD_FIELD_TYPES.CHART_TYPE) : ''
      });
      sendReportingInsightsUserFeedbackForReportingBackend({
        correlationId,
        userFeedback: I18n.text(`reporting-enablement.platformReport.reportVisualizationInsights.feedback.${feedbackType}.options.${key}`),
        contactForMoreFeedback: formState.contactMe,
        report,
        insight,
        dataset,
        feedbackValue: feedbackType === REPORTING_AI_ASSISTANT_FEEDBACK_TYPE.POSITIVE
      });
    });
    if (formState.otherFeedback.length) {
      usageTracker.track('reportingAIAssistantInsightFeedback', {
        feedbackValue: formState.otherFeedback,
        isPositiveFeedbackType: feedbackType === REPORTING_AI_ASSISTANT_FEEDBACK_TYPE.POSITIVE,
        contactMe: formState.contactMe,
        insight: insight.value,
        chartType: isValidForReportGetterStrategy ? getStandardFieldType(report, STANDARD_FIELD_TYPES.CHART_TYPE) : ''
      });
      sendReportingInsightsUserFeedbackForReportingBackend({
        correlationId,
        userFeedback: formState.otherFeedback,
        contactForMoreFeedback: formState.contactMe,
        report,
        insight,
        dataset,
        feedbackValue: feedbackType === REPORTING_AI_ASSISTANT_FEEDBACK_TYPE.POSITIVE
      });
    }
    if (feedbackType === REPORTING_AI_ASSISTANT_FEEDBACK_TYPE.NEGATIVE) {
      let insightsVerifyShouldRejectResult;
      if ('inaccurate' in formState.checkboxInputFeedback) {
        insightsVerifyShouldRejectResult = insight.verificationResult;
      } else {
        insightsVerifyShouldRejectResult = !insight.verificationResult;
      }
      if (insightsVerifyShouldRejectResult) {
        http.post(USER_FEEDBACK_URL, {
          data: [{
            correlationId: insight.correlationId,
            acceptedValues: [],
            rejectedValues: [insight.verificationResult]
          }]
        });
      } else {
        http.post(USER_FEEDBACK_URL, {
          data: [{
            correlationId: insight.correlationId,
            acceptedValues: [insight.verificationResult],
            rejectedValues: []
          }]
        });
      }
    }
  }, [setPopoverOpen, setDisableFeedback, formState.checkboxInputFeedback, formState.otherFeedback, formState.contactMe, feedbackType, usageTracker, insight, isValidForReportGetterStrategy, report, correlationId, dataset]);
  return /*#__PURE__*/_jsx(Popover, {
    "data-test-id": "reporting-ai-assistant-feedback-popover",
    open: popoverOpen,
    onOpenChange: () => setPopoverOpen(prevVal => !prevVal),
    content: {
      header: /*#__PURE__*/_jsx(H5, {
        "data-test-id": "reporting-ai-assistant-feedback-popover-title",
        children: title
      }),
      body: /*#__PURE__*/_jsx(FeedbackPopoverBody, {
        feedbackType: feedbackType,
        state: formState,
        dispatch: formDispatch
      }),
      footer: /*#__PURE__*/_jsx(Button, {
        "data-test-id": "reporting-ai-assistant-feedback-popover-close-button",
        disabled: !Object.keys(formState.checkboxInputFeedback).length && !formState.otherFeedback.length,
        use: "primary",
        onClick: onSubmitFeedbackClick,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-enablement.platformReport.reportVisualizationInsights.feedback.popover.submitFeedbackButton"
        })
      })
    },
    children: children
  });
};
export default FeedbackPopoverContainer;